/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materiach are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useCallback } from "react";
import APIManager from "../../../Services/ApiManager/ApiManager";

import "./GlobalPricingTable.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import { visuallyHidden } from "@mui/utils";
import RepeatIcon from "@mui/icons-material/Repeat";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PropTypes from "prop-types";
import GlobalDetailsPopup from "../GlobalDetailsPopup/GlobalDetailsPopup";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return order === 0 ? a[1] - b[1] : order;
  });
  return stabilizedThis.map((el) => el[0]);
}

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString();
};

const truncateText = (text, maxLength = 15) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const INAPP_WIDTH_MAP = {
  productId: "18%",
  price: "15%",
  trialPeriod: "15%",
  noOfPlans: "15%",
  lastModified: "37%",
};

const PREMIUM_WIDTH_MAP = {
  price: "15%",
  lastUpdated: "37%",
};

const getColumnWidth = (listType, headCellId) => {
  const widthMap = listType === "InApp" ? INAPP_WIDTH_MAP : PREMIUM_WIDTH_MAP;
  return widthMap[headCellId] || "auto";
};

const INAPP_HEAD_CELLS = [
  { id: "productId", numeric: false, disablePadding: false, label: "Product ID" },
  { id: "price", numeric: false, disablePadding: false, label: "Price" },
  { id: "trialPeriod", numeric: false, disablePadding: false, label: "Trial Period" },
  { id: "noOfPlans", numeric: false, disablePadding: false, label: "No. of Plans" },
  { id: "lastModified", numeric: false, disablePadding: false, label: "Last Modified" },
];

const PREMIUM_HEAD_CELLS = [
  { id: "name", numeric: false, disablePadding: false, label: "Plan name" },
  { id: "price", numeric: false, disablePadding: false, label: "Base price" },
  { id: "currencyCode", numeric: false, disablePadding: false, label: "Offers" },
  { id: "lastUpdated", numeric: false, disablePadding: false, label: "Last Modified" },
];

const REPEAT_ICON_SX = {
  marginLeft: 1,
  fontSize: 16,
  color: "#525252",
  fontWeight: 900,
  strokeWidth: 2,
  filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.3))',
};

function EnhancedTableHead({ order, orderBy, onRequestSort, headCells, listType }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: "#f3f3f3", height: "40px" }}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              backgroundColor: "#f3f3f3",
              fontWeight: "bold",
              padding: "8px 16px",
              whiteSpace: "nowrap",
              width: getColumnWidth(listType, headCell.id),
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontWeight: "bold" }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  headCells: PropTypes.array.isRequired,
  listType: PropTypes.string.isRequired,
};

function InAppRowCells({ row, formatDate, truncateText, openDetailsModal }) {
  const priceDisplay = row.type === "recurring" || row.price === "-"
    ? "-"
    : <span><span style={{ color: "#757677" }}>USD </span>{row.price}</span>;

  return (
    <>
      <TableCell component="th" scope="row" sx={{ padding: "6px 16px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={row.productId} arrow>
            <span style={{ fontWeight: "bold" }}>{truncateText(row.productId, 15)}</span>
          </Tooltip>
          {row.type === "recurring" && <RepeatIcon sx={REPEAT_ICON_SX} />}
        </div>
      </TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>{priceDisplay}</TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>
        {row.trialPeriod ? `${row.trialPeriod} Days` : "-"}
      </TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>{row.noOfPlans}</TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{formatDate(row.lastModified)}</span>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => openDetailsModal(row)}>
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </div>
      </TableCell>
    </>
  );
}

InAppRowCells.propTypes = {
  row: PropTypes.object.isRequired,
  formatDate: PropTypes.func.isRequired,
  truncateText: PropTypes.func.isRequired,
  openDetailsModal: PropTypes.func.isRequired,
};

function PremiumRowCells({ row, formatDate, openDetailsModal }) {
  return (
    <>
      <TableCell component="th" scope="row" sx={{ padding: "6px 16px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ fontWeight: "bold" }}>{row.name}</span>
          {row.type === "RECURRING" && <RepeatIcon sx={REPEAT_ICON_SX} />}
        </div>
      </TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>
        <span>
          <span style={{ color: "#757677" }}>{row.currencyCode} </span>
          {row.price?.toFixed(2)}
        </span>
      </TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>0</TableCell>
      <TableCell align="left" sx={{ padding: "6px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{formatDate(row.lastUpdated)}</span>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => openDetailsModal(row)}>
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </div>
      </TableCell>
    </>
  );
}

PremiumRowCells.propTypes = {
  row: PropTypes.object.isRequired,
  formatDate: PropTypes.func.isRequired,
  openDetailsModal: PropTypes.func.isRequired,
};

const transformPremiumRow = (row) => ({
  ...row,
  id: row.productId,
  productId: row.productId,
  planId: row.planId || row.id,
  type: row.type === "ONETIME" ? "oneTime" : "recurring",
  price: row.price,
  currencyCode: row.currencyCode,
  name: row.name,
});

function GlobalPricingTable(props) {
  const { listType, data } = props;

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [dense] = React.useState(false);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);

  const headCells = listType === "InApp" ? INAPP_HEAD_CELLS : PREMIUM_HEAD_CELLS;

  useEffect(() => {
    setRows(data);
    if (data?.length > 0) {
      setOrder("desc");
      setOrderBy("lastModified");
    }
  }, [data]);

  const getComparator = useCallback((order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDetailsModal = async (row) => {
    if (listType !== "Premium") {
      setSelectedProduct(row);
      setShowDetailsModal(true);
      return;
    }

    const transformed = transformPremiumRow(row);
    try {
      await APIManager.getPlanDetails(row.productId, row.planId || row.id);
      setSelectedProduct(transformed);
    } catch {
      setSelectedProduct(transformed);
    }
    setShowDetailsModal(true);
  };

  const handleCloseDetailsPopup = () => {
    setShowDetailsModal(false);
    setSelectedProduct(null);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows, getComparator]
  );

  return (
    <Box sx={{ width: "100%", marginTop: "16px" }}>
      <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
        <TableContainer elevation={0}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={headCells}
              listType={listType}
            />
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={listType === "InApp" ? row.id : row.planId}
                  sx={{ cursor: "pointer", height: "45px" }}
                >
                  {listType === "InApp" ? (
                    <InAppRowCells row={row} formatDate={formatDate} truncateText={truncateText} openDetailsModal={openDetailsModal} />
                  ) : (
                    <PremiumRowCells row={row} formatDate={formatDate} openDetailsModal={openDetailsModal} />
                  )}
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={headCells.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Items per page"
        />
      </Paper>
      <Modal
        open={showDetailsModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 750,
            bgcolor: "background.paper",
            borderRadius: "4px",
            boxShadow: 24,
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          {selectedProduct && (
            <GlobalDetailsPopup
              onClose={handleCloseDetailsPopup}
              row={selectedProduct}
              listType={listType}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}

GlobalPricingTable.propTypes = {
  data: PropTypes.array.isRequired,
  listType: PropTypes.string.isRequired,
};

export default GlobalPricingTable;